import {Alert, AlertColor, Snackbar} from '@mui/material'
import React, {createContext, useCallback, useContext, useMemo, useState} from 'react'

interface SnackbarMessage {
  id: number
  message: string
  severity: AlertColor
}

interface GlobalSnackbarContextType {
  /**
   * Show a success message (green)
   * @param message - The message to display
   */
  showSuccess: (message: string) => void

  /**
   * Show an error message (red)
   * Handles RTK Query errors, Error objects, or plain strings
   * @param error - Error object or string message
   */
  showError: (error: unknown) => void

  /**
   * Show a warning message (orange)
   * @param message - The message to display
   */
  showWarning: (message: string) => void

  /**
   * Show an info message (blue)
   * @param message - The message to display
   */
  showInfo: (message: string) => void
}

const GlobalSnackbarContext = createContext<GlobalSnackbarContextType | undefined>(undefined)

// Error type definitions matching useSnackbarWithComponent
interface ApiError {
  response?: {
    data?: {
      customData?: {
        title?: string
      }
      message?: string
    }
  }
  data?: {
    customData?: {
      title?: string
    }
    message?: string
  }
  message?: string
}

/**
 * Extract error message from various error formats
 * Matches the logic from useSnackbarWithComponent for consistency
 * Priority: customData.title > response.data.message > data.message > message > fallback
 */
const extractErrorMessage = (error: unknown): string => {
  // Handle string errors
  if (typeof error === 'string') {
    return error
  }

  // Cast to ApiError type for easier access
  const err = error as ApiError

  // Check for custom error title first (highest priority) - Axios style
  const customTitle = err.response?.data?.customData?.title
  if (customTitle && customTitle.length > 0) {
    return customTitle
  }

  // Check for standard error message in response - Axios style
  const responseMessage = err.response?.data?.message
  if (responseMessage && responseMessage.length > 0) {
    return responseMessage
  }

  // Check for RTK Query style data.customData.title
  const dataCustomTitle = err.data?.customData?.title
  if (dataCustomTitle && dataCustomTitle.length > 0) {
    return dataCustomTitle
  }

  // Check for RTK Query style data.message
  const dataMessage = err.data?.message
  if (dataMessage && dataMessage.length > 0) {
    return dataMessage
  }

  // Check for direct error message
  const directMessage = err.message
  if (directMessage && directMessage.length > 0) {
    return directMessage
  }

  return 'An unexpected error occurred'
}

interface GlobalSnackbarProviderProps {
  children: React.ReactNode
  /**
   * Auto-hide duration in milliseconds (default: 5000)
   */
  autoHideDuration?: number
}

export function GlobalSnackbarProvider({
  children,
  autoHideDuration = 5000,
}: GlobalSnackbarProviderProps) {
  const [currentMessage, setCurrentMessage] = useState<SnackbarMessage | null>(null)
  const [open, setOpen] = useState(false)

  // Show message immediately, replacing any existing message
  const addMessage = useCallback((message: string, severity: AlertColor) => {
    const newMessage: SnackbarMessage = {
      id: Date.now(),
      message,
      severity,
    }

    // Always replace immediately - no queuing
    setCurrentMessage(newMessage)
    setOpen(true)
  }, [])

  const handleClose = useCallback((_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return
    }
    setOpen(false)
  }, [])

  const handleExited = useCallback(() => {
    setCurrentMessage(null)
  }, [])

  const showSuccess = useCallback(
    (message: string) => {
      addMessage(message, 'success')
    },
    [addMessage],
  )

  const showError = useCallback(
    (error: unknown) => {
      const message = extractErrorMessage(error)
      addMessage(message, 'error')
    },
    [addMessage],
  )

  const showWarning = useCallback(
    (message: string) => {
      addMessage(message, 'warning')
    },
    [addMessage],
  )

  const showInfo = useCallback(
    (message: string) => {
      addMessage(message, 'info')
    },
    [addMessage],
  )

  // Memoize context value to prevent unnecessary re-renders of consumers
  // when the snackbar state (open, currentMessage) changes
  const contextValue: GlobalSnackbarContextType = useMemo(
    () => ({
      showSuccess,
      showError,
      showWarning,
      showInfo,
    }),
    [showSuccess, showError, showWarning, showInfo],
  )
  return (
    <GlobalSnackbarContext.Provider value={contextValue}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={autoHideDuration}
        onClose={handleClose}
        TransitionProps={{onExited: handleExited}}
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}>
        <Alert
          onClose={handleClose}
          severity={currentMessage?.severity ?? 'info'}
          variant="filled"
          sx={{width: '100%', minWidth: '300px'}}>
          {currentMessage?.message}
        </Alert>
      </Snackbar>
    </GlobalSnackbarContext.Provider>
  )
}

/**
 * Hook to access global snackbar functions
 *
 * @example
 * ```tsx
 * const { showSuccess, showError } = useGlobalSnackbar()
 *
 * // In a try/catch block
 * try {
 *   await createJobOrder({...}).unwrap()
 *   showSuccess('Job Order created successfully!')
 *   navigate('/job-orders')
 * } catch (error) {
 *   showError(error)
 * }
 * ```
 */
export function useGlobalSnackbar(): GlobalSnackbarContextType {
  const context = useContext(GlobalSnackbarContext)

  if (!context) {
    throw new Error('useGlobalSnackbar must be used within a GlobalSnackbarProvider')
  }

  return context
}
